import { useDataChange, useDataValue } from 'Simple/Data.js'
import React, { useMemo } from 'react'
import View from './view.js'
import {
  useNotifications,
  notifyError,
  notifySuccess,
} from 'Logic/Notifications'
import TysiaCheckbox from 'DesignSystem/TysiaCheckbox/index.js'
import TysiaCheckboxInput from 'DesignSystem/TysiaCheckbox/TysiaCheckboxInput/index.js'
import { useMutation } from 'Data/Api.js'
import mutation from './mutation.graphql.js'
import migrate_appointment_reminders from './mutation-migrate-reminders.graphql.js'

export default function Logic(props) {
  let feature_flags = useDataValue({
    context: 'feature_flags',
    viewPath: props.viewPath,
  })
  let change = useDataChange({
    context: 'feature_flags',
    viewPath: props.viewPath,
  })
  let [, executeMutation] = useMutation(mutation)
  let [, executeMutationMigrateReminders] = useMutation(
    migrate_appointment_reminders
  )
  let [, notify] = useNotifications()

  let value = useMemo(() => {
    return {
      columns: [
        {
          header: 'Name',
          accessorKey: 'name',
        },
        {
          header: 'Level',
          accessorKey: 'level',
        },
        {
          header: 'Beta calendar', // it's the headers that are not grouping
          columns: [
            {
              header: 'Default',
              accessorKey: 'new_calendar_default_enabled',
              cell: cell => (
                <TysiaCheckbox
                  marginLeft={4}
                  marginRight={4}
                  disabled={isDisabled(cell)}
                  onChange={value => onChange(cell, value)}
                  value={cell.getValue()}
                  viewPath={props.viewPath}
                >
                  {childProps => <TysiaCheckboxInput {...childProps} />}
                </TysiaCheckbox>
              ),
            },
          ],
        },
        {
          header: 'Leads App',
          columns: [
            {
              header: 'Default',
              accessorKey: 'new_leads_settings_enabled',
              cell: cell => (
                <TysiaCheckbox
                  marginLeft={4}
                  marginRight={4}
                  disabled={isDisabled(cell)}
                  onChange={value => onChange(cell, value)}
                  value={cell.getValue()}
                  viewPath={props.viewPath}
                >
                  {childProps => <TysiaCheckboxInput {...childProps} />}
                </TysiaCheckbox>
              ),
            },
          ],
        },
        {
          header: 'Automation hub communication action',
          columns: [
            {
              header: 'Enabled',
              accessorKey: 'new_automation-hub_comms_enabled',
              cell: cell => (
                <TysiaCheckbox
                  marginLeft={4}
                  marginRight={4}
                  disabled={isDisabled(cell)}
                  onChange={value => onChange(cell, value)}
                  value={cell.getValue()}
                  viewPath={props.viewPath}
                >
                  {childProps => <TysiaCheckboxInput {...childProps} />}
                </TysiaCheckbox>
              ),
            },
          ],
        },
        {
          header: 'AI Recorder',
          columns: [
            {
              header: 'Enabled',
              accessorKey: 'new_ai_recorder_enabled',
              cell: cell => (
                <TysiaCheckbox
                  marginLeft={4}
                  marginRight={4}
                  disabled={isDisabled(cell)}
                  onChange={value => onChange(cell, value)}
                  value={cell.getValue()}
                  viewPath={props.viewPath}
                >
                  {childProps => <TysiaCheckboxInput {...childProps} />}
                </TysiaCheckbox>
              ),
            },
          ],
        },
        {
          header: 'Beta Tasks',
          columns: [
            {
              header: 'Default',
              accessorKey: 'new_tasks_enabled',
              cell: cell => (
                <TysiaCheckbox
                  marginLeft={4}
                  marginRight={4}
                  disabled={isDisabled(cell)}
                  onChange={value => onChange(cell, value)}
                  value={cell.getValue()}
                  viewPath={props.viewPath}
                >
                  {childProps => <TysiaCheckboxInput {...childProps} />}
                </TysiaCheckbox>
              ),
            },
          ],
        },
        {
          header: 'Beta Generate Letters',
          columns: [
            {
              header: 'Default',
              accessorKey: 'new_generate-letters_enabled',
              cell: cell => (
                <TysiaCheckbox
                  marginLeft={4}
                  marginRight={4}
                  disabled={isDisabled(cell)}
                  onChange={value => onChange(cell, value)}
                  value={cell.getValue()}
                  viewPath={props.viewPath}
                >
                  {childProps => <TysiaCheckboxInput {...childProps} />}
                </TysiaCheckbox>
              ),
            },
          ],
        },
        {
          header: 'Beta Search',
          columns: [
            {
              header: 'Enabled',
              accessorKey: 'new_search_enabled',
              cell: cell => (
                <TysiaCheckbox
                  marginLeft={4}
                  marginRight={4}
                  disabled={isDisabled(cell)}
                  onChange={value => onChange(cell, value)}
                  value={cell.getValue()}
                  viewPath={props.viewPath}
                >
                  {childProps => <TysiaCheckboxInput {...childProps} />}
                </TysiaCheckbox>
              ),
            },
          ],
        },
      ],
      data: feature_flags || [],
    }

    async function onChange(cell, value) {
      change(next => {
        let item = next.find(item => item.id === cell.row.original.id)
        item[cell.column.id] = value
      })

      let res = await executeMutation({
        organization_id: cell.row.original.id,
        message_key: cell.column.id.replaceAll('_', '.'),
        enabled: value,
      })

      let featureFlagName = cell.column.parent
        ? `${
            cell.column.parent.columnDef.header
          } ${cell.column.columnDef.header.toLowerCase()}`
        : cell.column.columnDef.header

      if (res.error) {
        return notify(
          notifyError(
            `Can't toggle "${featureFlagName}" ${value ? 'on' : 'off'} at ${
              cell.row.original.name
            }. Contact support if the problem persists.`
          )
        )
      }

      notify(
        notifySuccess(
          `Toggled "${featureFlagName}" ${value ? 'on' : 'off'} at ${
            cell.row.original.name
          }`
        )
      )

      if (value && cell.column.id === 'new_automation-hub_comms_enabled') {
        let migrationRes = await executeMutationMigrateReminders()
        if (migrationRes.error) {
          return notify(
            notifyError('Something went wrong with migrating appt reminders.')
          )
        }
        if (!migrationRes.data.appt_reminders_migrate.ok) {
          return notify(
            notifyError(
              `Something went wrong with migrating appt reminders. ${migrationRes.data.appt_reminders_migrate.message}`
            )
          )
        }
        notify(notifySuccess('Appt reminders migrated.'))
      }
    }

    function isDisabled(cell) {
      if (cell.row.original.level === 'parent') return false

      return cell.table
        .getRowModel()
        .rows.find(item => item.original.level === 'parent')?.original[
        cell.column.id
      ]
    }
  }, [feature_flags]) // eslint-disable-line
  // ignore executeMutation, change, notify and props.viewPath

  return <View value={value} viewPath={props.viewPath} />
}
